import { useMemo, useState } from 'react';
import { useFragment } from '@apollo/client';
import {
  ProjectWorkspaceInfo,
  type ProjectWorkspaceInfoFragment,
  useGetProjectForNavigationQuery,
  type Workspace,
} from '@frameio/graphql-client/generated';
import type { WorkspaceProjectList } from '@frameio/workspaces/shared/components/WorkspaceProjectList';

/**
 * Hook that manages workspace and project data for export location selection.
 * Retrieves project information via GraphQL fragment and maintains a mapping of
 * projects to workspaces for efficient lookup. Handles cases where workspace
 * data may come from either the fragment or from user selection.
 *
 * @param projectId - The ID of the project to fetch workspace info for, or null
 * if no project is selected
 * @returns An object containing project data, associated workspace, and
 * selection handler
 * @returns project - The project data with workspace info, or null if not found
 * @returns error - The project lookup error, if the selected project could not be loaded
 * @returns workspace - The workspace associated with the project, resolved from
 * fragment or selection map
 * @returns onWorkspaceProjectSelect - Callback to handle workspace/project
 * selection and update the internal map
 */
export const useWorkspaceProject = (projectId: string | null) => {
  const [projectIdWorkspaceMap, setProjectIdWorkspaceMap] = useState<
    Record<string, Workspace>
  >({});

  const { data: maybeProject } = useFragment({
    fragment: ProjectWorkspaceInfo,
    fragmentName: 'ProjectWorkspaceInfo',
    from: {
      __typename: 'Project',
      id: projectId,
    },
  });

  const shouldFetchProject = Boolean(projectId && !maybeProject?.rootAssetId);

  // When a projectId is set but the project isn't fully in the cache yet, fetch
  // it directly. GetProjectForNavigation spreads ProjectWorkspaceInfo, so this
  // populates the fragment above. Skips once the project (incl. rootAssetId) is
  // present, so normal click-through flows never fire it.
  const { error } = useGetProjectForNavigationQuery({
    variables: { projectId: projectId ?? '' },
    skip: !shouldFetchProject,
  });

  const project = maybeProject?.id
    ? (maybeProject as ProjectWorkspaceInfoFragment)
    : null;

  const onWorkspaceProjectSelect: React.ComponentProps<
    typeof WorkspaceProjectList
  >['onWorkspaceProjectSelect'] = (value) => {
    const { project: p, workspace } = value;
    setProjectIdWorkspaceMap((prev) => ({
      ...prev,
      [p.id]: workspace as Workspace,
    }));
  };

  const workspace = useMemo(() => {
    if (project?.workspace) {
      return project.workspace;
    }
    if (project?.id) {
      return projectIdWorkspaceMap[project.id] ?? null;
    }
    return null;
  }, [project, projectIdWorkspaceMap]);

  return {
    project,
    error,
    workspace,
    onWorkspaceProjectSelect,
  };
};
